import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Bell,
  Check,
  CheckCheck,
  Trash2,
  Info,
  AlertTriangle,
  CheckCircle,
  AlertCircle,
  Clock
} from 'lucide-react'
import { apiService } from '../../services/api'
import { useAuth } from '../../context/AuthContext'
import { Button } from '../ui/button'
import { Card, CardContent } from '../ui/card'

// ── Helpers ─────────────────────────────────────────────────────────────────

const timeAgo = (timestamp) => {
  const diff = Date.now() - new Date(timestamp).getTime()
  const mins = Math.floor(diff / 60000)
  const hrs  = Math.floor(diff / 3600000)
  const days = Math.floor(diff / 86400000)
  if (mins < 1)  return 'Just now'
  if (mins < 60) return `${mins}m ago`
  if (hrs < 24)  return `${hrs}h ago`
  if (days < 7)  return `${days}d ago`
  return new Date(timestamp).toLocaleDateString()
}

const extractId = (url = '') => {
  const match = url.match(/\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i)
  return match ? match[1] : null
}

const typeConfig = {
  success: { icon: CheckCircle, color: 'var(--success)',  label: 'Success' },
  warning: { icon: AlertTriangle, color: 'var(--warning)', label: 'Warning' },
  error:   { icon: AlertCircle,   color: 'var(--danger)',  label: 'Error'   },
  info:    { icon: Info,          color: 'var(--accent)',  label: 'Info'    },
}

const getTypeConfig = (type) => typeConfig[type] || typeConfig.info

// ── Main component ───────────────────────────────────────────────────────────

const NotificationsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [statusFilter, setStatusFilter] = useState(searchParams.get('filter') || 'all')
  const [typeFilter,   setTypeFilter]   = useState(searchParams.get('type')   || 'all')
  const queryClient = useQueryClient()
  const navigate    = useNavigate()
  const { user }    = useAuth()
  const role        = user?.role

  // ── Data ──────────────────────────────────────────────────────────────────

  const { data: notificationsData, isLoading } = useQuery(
    ['notifications-page', typeFilter],
    () => {
      const params = new URLSearchParams()
      if (typeFilter !== 'all') params.append('type', typeFilter)
      params.append('limit', '100')
      return apiService.get(`/notifications?${params.toString()}`).then(r => r.data)
    },
    { staleTime: 30000, retry: 1, refetchOnWindowFocus: false }
  )

  // ── Mutations ─────────────────────────────────────────────────────────────

  const invalidate = () => {
    queryClient.invalidateQueries('notifications-page')
    queryClient.invalidateQueries('notifications-unread-count')
    queryClient.invalidateQueries('notifications')
  }

  const markAsReadMutation = useMutation(
    (id) => apiService.patch(`/notifications/${id}/read`),
    { onSuccess: invalidate }
  )

  const markAllAsReadMutation = useMutation(
    () => apiService.patch('/notifications/mark-all-read'),
    { onSuccess: invalidate }
  )

  const deleteNotificationMutation = useMutation(
    (id) => apiService.delete(`/notifications/${id}`),
    { onSuccess: invalidate }
  )

  // ── Routing ───────────────────────────────────────────────────────────────

  const getLink = (notification) => {
    const data = notification.data
      ? (typeof notification.data === 'string' ? JSON.parse(notification.data) : notification.data)
      : {}
    const actionUrl = data.actionUrl || ''
    const title   = notification.title.toLowerCase()
    const message = notification.message.toLowerCase()

    if (
      title.includes('edit request') || message.includes('edit request') ||
      notification.type === 'edit_request' || actionUrl.includes('/requests')
    ) {
      const id = data.requestId || extractId(actionUrl)
      if (role === 'admin') return id ? `/admin/requests?open=${id}` : '/admin/requests'
      return id ? `/client/requests?open=${id}` : '/client/requests'
    }

    if (
      title.includes('support') || message.includes('support') ||
      notification.type === 'support' || notification.type === 'info' ||
      actionUrl.includes('/support')
    ) {
      const id = data.ticketId || extractId(actionUrl)
      if (role === 'admin') return id ? `/admin/support?open=${id}` : '/admin/support'
      return id ? `/client/support?open=${id}` : '/client/support'
    }

    if (
      title.includes('site published') || message.includes('published') ||
      notification.type === 'site_published'
    ) {
      if (role === 'admin') return data.siteId ? `/admin/sites/${data.siteId}` : '/admin/sites'
      return data.siteId ? `/client/sites/${data.siteId}` : '/client/sites'
    }

    if (
      title.includes('payment') || message.includes('payment') ||
      title.includes('billing') || notification.type === 'billing'
    ) {
      if (role === 'admin') return data.clientId ? `/admin/billing?client=${data.clientId}` : '/admin/billing'
      return '/client/billing'
    }

    if (actionUrl && !actionUrl.includes('localhost') && !actionUrl.includes('/dashboard')) {
      return actionUrl
    }

    return role === 'admin' ? '/admin' : '/client'
  }

  const handleClick = (notification) => {
    if (!notification.is_read) markAsReadMutation.mutate(notification.id)
    const link = getLink(notification)
    if (link && link !== window.location.pathname) navigate(link)
  }

  // ── Derived state ─────────────────────────────────────────────────────────

  const allNotifications = notificationsData?.notifications || []

  const displayed = allNotifications.filter(n => {
    if (statusFilter === 'unread') return !n.is_read
    if (statusFilter === 'read')   return  n.is_read
    return true
  })

  const unreadCount = allNotifications.filter(n => !n.is_read).length
  const countFor    = (f) => f === 'all' ? allNotifications.length
    : f === 'unread' ? allNotifications.filter(n => !n.is_read).length
    : allNotifications.filter(n => n.is_read).length

  const updateStatusFilter = (f) => {
    setStatusFilter(f)
    setSearchParams({ filter: f, type: typeFilter })
  }

  const updateTypeFilter = (t) => {
    setTypeFilter(t)
    setSearchParams({ filter: statusFilter, type: t })
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', paddingTop: '1rem' }}>

      {/* Header */}
      <div className="flex items-center justify-between" style={{ flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <div className="flex items-center gap-3">
            <h1 className="font-bold" style={{ fontSize: '1.875rem' }}>
              Notifications
            </h1>
            {unreadCount > 0 && (
              <span className="badge badge-accent">
                {unreadCount} unread
              </span>
            )}
          </div>
          <p className="text-muted" style={{ marginTop: '0.25rem' }}>
            All your notifications in one place.
          </p>
        </div>

        {unreadCount > 0 && (
          <Button
            variant="outline"
            onClick={() => markAllAsReadMutation.mutate()}
            disabled={markAllAsReadMutation.isLoading}
            className="flex items-center gap-2"
          >
            <CheckCheck style={{ width: '1rem', height: '1rem' }} />
            Mark all as read
          </Button>
        )}
      </div>

      {/* Filter toolbar */}
      <Card>
        <CardContent style={{ padding: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>

            {/* Status tabs */}
            <div className="flex items-center gap-1">
              {['all', 'unread', 'read'].map((f) => (
                <button
                  key={f}
                  onClick={() => updateStatusFilter(f)}
                  style={{
                    padding: '0.375rem 0.75rem',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    borderRadius: '0.5rem',
                    border: 'none',
                    cursor: 'pointer',
                    textTransform: 'capitalize',
                    transition: 'all 0.2s',
                    background: statusFilter === f ? 'var(--accent)' : 'transparent',
                    color: statusFilter === f ? '#fff' : 'var(--text-muted)',
                  }}
                >
                  {f}
                  {countFor(f) > 0 && (
                    <span style={{
                      marginLeft: '0.375rem',
                      fontSize: '0.75rem',
                      opacity: statusFilter === f ? 0.8 : 0.6,
                    }}>
                      {countFor(f)}
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* Divider */}
            <div style={{ width: '1px', height: '1.25rem', background: 'var(--border)' }} />

            {/* Type filter */}
            <div className="flex items-center gap-1" style={{ flexWrap: 'wrap' }}>
              {['all', 'info', 'success', 'warning', 'error'].map((t) => {
                const cfg = t !== 'all' ? getTypeConfig(t) : null
                return (
                  <button
                    key={t}
                    onClick={() => updateTypeFilter(t)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.375rem',
                      padding: '0.375rem 0.75rem',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      borderRadius: '0.5rem',
                      border: 'none',
                      cursor: 'pointer',
                      textTransform: 'capitalize',
                      transition: 'all 0.2s',
                      background: typeFilter === t ? 'var(--bg-1)' : 'transparent',
                      color: typeFilter === t ? 'var(--text)' : 'var(--text-muted)',
                    }}
                  >
                    {cfg && (
                      <span style={{ width: '0.5rem', height: '0.5rem', borderRadius: '50%', background: cfg.color, flexShrink: 0 }} />
                    )}
                    {t}
                  </button>
                )
              })}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Notification list */}
      <Card className="overflow-hidden">
        {isLoading ? (
          <CardContent style={{ padding: '4rem 1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem' }} className="text-muted">
            <div style={{
              width: '1.25rem',
              height: '1.25rem',
              border: '2px solid var(--accent)',
              borderTopColor: 'transparent',
              borderRadius: '50%',
              animation: 'spin 0.7s linear infinite',
            }} />
            <span style={{ fontSize: '0.875rem' }}>Loading notifications…</span>
          </CardContent>
        ) : displayed.length === 0 ? (
          <CardContent style={{ padding: '4rem 1rem', textAlign: 'center' }}>
            <div style={{
              width: '3.5rem',
              height: '3.5rem',
              margin: '0 auto 1rem',
              background: 'var(--bg-1)',
              borderRadius: '0.75rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <Bell style={{ width: '1.75rem', height: '1.75rem', color: 'var(--text-faint)' }} />
            </div>
            <p className="font-medium" style={{ fontSize: '1rem' }}>
              {statusFilter === 'unread' ? "You're all caught up!" : 'No notifications'}
            </p>
            <p className="text-faint" style={{ fontSize: '0.875rem', marginTop: '0.25rem' }}>
              {statusFilter === 'unread'
                ? 'Nothing unread right now.'
                : 'Notifications will appear here when something happens.'}
            </p>
          </CardContent>
        ) : (
          <div>
            {displayed.map((notification, idx) => {
              const cfg = getTypeConfig(notification.type)
              const Icon = cfg.icon
              const isLast = idx === displayed.length - 1

              return (
                <div
                  key={notification.id}
                  onClick={() => handleClick(notification)}
                  className="cursor-pointer"
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '1rem',
                    padding: !notification.is_read ? '1rem 1.25rem 1rem calc(1.25rem - 3px)' : '1rem 1.25rem',
                    borderBottom: isLast ? 'none' : '1px solid var(--border)',
                    borderLeft: !notification.is_read ? '3px solid var(--accent)' : '3px solid transparent',
                    transition: 'background 0.15s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-1)'}
                  onMouseLeave={e => e.currentTarget.style.background = ''}
                >
                  {/* Type icon */}
                  <div style={{
                    flexShrink: 0,
                    width: '2.25rem',
                    height: '2.25rem',
                    borderRadius: '0.75rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginTop: '0.125rem',
                    background: `color-mix(in srgb, ${cfg.color} 15%, transparent)`,
                  }}>
                    <Icon style={{ width: '1rem', height: '1rem', color: cfg.color }} />
                  </div>

                  {/* Content */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-2" style={{ minWidth: 0 }}>
                        <p className={['font-semibold truncate', notification.is_read ? 'text-muted' : ''].filter(Boolean).join(' ')}
                           style={{ fontSize: '0.875rem' }}>
                          {notification.title}
                        </p>
                        {!notification.is_read && (
                          <span style={{
                            flexShrink: 0,
                            width: '0.5rem',
                            height: '0.5rem',
                            borderRadius: '50%',
                            background: 'var(--accent)',
                          }} />
                        )}
                      </div>

                      {/* Hover actions */}
                      <div className="flex items-center gap-1" style={{ flexShrink: 0, opacity: 0 }}
                           onMouseEnter={e => e.currentTarget.style.opacity = '1'}
                           onMouseLeave={e => e.currentTarget.style.opacity = '0'}>
                        {!notification.is_read && (
                          <button
                            onClick={(e) => { e.stopPropagation(); markAsReadMutation.mutate(notification.id) }}
                            style={{
                              padding: '0.375rem',
                              borderRadius: '0.5rem',
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              color: 'var(--text-muted)',
                            }}
                            title="Mark as read"
                          >
                            <Check style={{ width: '0.875rem', height: '0.875rem' }} />
                          </button>
                        )}
                        <button
                          onClick={(e) => { e.stopPropagation(); deleteNotificationMutation.mutate(notification.id) }}
                          style={{
                            padding: '0.375rem',
                            borderRadius: '0.5rem',
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            color: 'var(--text-muted)',
                          }}
                          title="Delete"
                        >
                          <Trash2 style={{ width: '0.875rem', height: '0.875rem' }} />
                        </button>
                      </div>
                    </div>

                    <p className="text-muted" style={{ fontSize: '0.875rem', marginTop: '0.125rem', lineHeight: '1.5' }}>
                      {notification.message}
                    </p>

                    <div className="flex items-center gap-1 text-faint" style={{ marginTop: '0.375rem', fontSize: '0.75rem' }}>
                      <Clock style={{ width: '0.75rem', height: '0.75rem' }} />
                      {timeAgo(notification.created_at)}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </Card>
    </div>
  )
}

export default NotificationsPage
